import React, { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined
} from '@ant-design/icons'
import userService from '../services/userService'
import { User, UserFilter, UserRole } from '../types/users'
import {
  Typography,
  Button,
  Space,
  Card,
  Row,
  Col,
  Spin,
  Empty,
  Table,
  Tag,
  Switch,
  Tooltip,
  Input,
  Select,
  Avatar,
  Popconfirm
} from 'antd'
import debounce from 'lodash/debounce'
import { useSnackbar } from 'notistack'

const { Title } = Typography
const { Search } = Input
const { Option } = Select

const Users: React.FC = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [filter, setFilter] = useState<UserFilter>({})
  const { enqueueSnackbar } = useSnackbar()

  // Tạo hàm debounce cho việc tìm kiếm
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setFilter(prev => ({
        ...prev,
        search: value.trim()
      }))
    }, 1000),
    []
  )

  // Xử lý thay đổi bộ lọc tìm kiếm
  const handleSearch = (value: string) => {
    debouncedSearch(value)
  }

  // Cleanup debounce khi component unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  // Lấy danh sách người dùng theo bộ lọc
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true)
      try {
        const result = await userService.getUsers(filter)
        setUsers(result.data)
      } catch (error) {
        console.error('Lỗi khi lấy danh sách người dùng:', error)
        enqueueSnackbar('Có lỗi xảy ra khi tải danh sách người dùng', { variant: 'error' })
      } finally {
        setIsLoading(false)
      }
    }

    fetchUsers()
  }, [filter, enqueueSnackbar])

  // Xử lý thay đổi bộ lọc role
  const handleRoleFilterChange = (value: UserRole | undefined) => {
    setFilter({
      ...filter,
      role: value
    })
  }

  // Xử lý thay đổi bộ lọc trạng thái
  const handleStatusFilterChange = (value: boolean | undefined) => {
    setFilter({
      ...filter,
      isActive: value
    })
  }

  // Xử lý xóa tất cả filter
  const handleClearFilter = () => {
    setFilter({})
  }

  // Xử lý khi xóa người dùng
  const handleDelete = async (userId: string) => {
    try {
      await userService.deleteUser(userId)
      setUsers(users.filter(user => user._id !== userId))
      enqueueSnackbar('Đã xóa người dùng thành công', { variant: 'success' })
    } catch (error: any) {
      console.error('Lỗi khi xóa người dùng:', error)
      enqueueSnackbar(error.message || 'Có lỗi xảy ra khi xóa người dùng', { variant: 'error' })
    }
  }

  // Xử lý bật/tắt trạng thái người dùng
  const handleToggleActive = async (userId: string, isActive: boolean) => {
    try {
      await userService.updateUser({
        id: userId,
        isActive: !isActive
      })
      setUsers(
        users.map(user =>
          user._id === userId
            ? { ...user, isActive: !isActive }
            : user
        )
      )
      enqueueSnackbar(`Đã ${!isActive ? 'kích hoạt' : 'vô hiệu hóa'} người dùng thành công`, { variant: 'success' })
    } catch (error) {
      console.error('Lỗi khi cập nhật trạng thái người dùng:', error)
      enqueueSnackbar('Có lỗi xảy ra khi cập nhật trạng thái người dùng', { variant: 'error' })
    }
  }

  // Xử lý làm mới dữ liệu
  const handleRefresh = () => {
    setIsLoading(true)
    userService
      .getUsers(filter)
      .then(result => {
        setUsers(result.data)
      })
      .catch(error => {
        console.error('Lỗi khi làm mới danh sách người dùng:', error)
        enqueueSnackbar('Có lỗi xảy ra khi làm mới danh sách người dùng', { variant: 'error' })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  // Hiển thị tag cho vai trò người dùng
  const getRoleTag = (role: UserRole) => {
    switch (role) {
      case 'admin':
        return <Tag color='red'>Quản trị viên</Tag>
      case 'user':
        return <Tag color='blue'>Người dùng</Tag>
      default:
        return <Tag>Không xác định</Tag>
    }
  }

  // Cấu hình các cột cho bảng
  const columns = [
    {
      title: 'Người dùng',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (text: string, record: User) => (
        <div className='flex items-center'>
          <Avatar
            icon={<UserOutlined />}
            size='large'
            className='mr-3'
          />
          <div>
            <div className='font-medium'>{text}</div>
            <div className='text-xs text-gray-500'>{record.email}</div>
            {record.phone && (
              <div className='text-xs text-gray-500'>{record.phone}</div>
            )}
          </div>
        </div>
      )
    },
    {
      title: 'Vai trò',
      dataIndex: 'role',
      key: 'role',
      render: (role: UserRole) => getRoleTag(role)
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      align: 'center' as const,
      render: (isActive: boolean, record: User) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleActive(record._id, isActive)}
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<CloseCircleOutlined />}
        />
      )
    },
    {
      title: 'Thao tác',
      key: 'action',
      align: 'center' as const,
      render: (_: any, record: User) => (
        <Space>
          <Tooltip title='Chỉnh sửa'>
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/admin/users/${record._id}`)}
              type='text'
            />
          </Tooltip>
          <Tooltip title='Xóa'>
            <Popconfirm
              title='Xóa người dùng'
              description='Bạn có chắc chắn muốn xóa người dùng này?'
              onConfirm={() => handleDelete(record._id)}
              okText='Xóa'
              cancelText='Hủy'
            >
              <Button
                icon={<DeleteOutlined />}
                danger
                type='text'
                disabled={record.role === 'admin'}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ]

  return (
    <div className='p-6'>
      <Row justify='space-between' align='middle' className='mb-6'>
        <Col>
          <Title level={2} className='m-0'>
            Quản lý người dùng
          </Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined spin={isLoading} />}
              onClick={handleRefresh}
              disabled={isLoading}
            />
            <Link to='/admin/users/new'>
              <Button
                type='primary'
                icon={<PlusOutlined />}
                className='bg-green-600 border-green-600 hover:bg-green-700'
              >
                Thêm người dùng mới
              </Button>
            </Link>
          </Space>
        </Col>
      </Row>

      {/* Phần lọc */}
      <Card className='mb-6'>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <div className='mb-4'>
              <label className='block mb-1 text-sm font-medium text-gray-700'>
                Tìm kiếm
              </label>
              <Search
                placeholder='Tìm theo tên, email, số điện thoại'
                allowClear
                onSearch={handleSearch}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div className='mb-4'>
              <label className='block mb-1 text-sm font-medium text-gray-700'>
                Vai trò
              </label>
              <Select
                style={{ width: '100%' }}
                placeholder='Lọc theo vai trò'
                allowClear
                onChange={handleRoleFilterChange}
              >
                <Option value='admin'>Quản trị viên</Option>
                <Option value='user'>Người dùng</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div className='mb-4'>
              <label className='block mb-1 text-sm font-medium text-gray-700'>
                Trạng thái
              </label>
              <Select
                style={{ width: '100%' }}
                placeholder='Lọc theo trạng thái'
                allowClear
                onChange={handleStatusFilterChange}
              >
                <Option value={true}>Đang hoạt động</Option>
                <Option value={false}>Ngưng hoạt động</Option>
              </Select>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Nội dung chính */}
      {isLoading ? (
        <div className='flex justify-center items-center h-64'>
          <Spin size='large' tip='Đang tải...' />
        </div>
      ) : users.length > 0 ? (
        <Card>
          <Table
            columns={columns}
            dataSource={users.map(user => ({
              ...user,
              key: user._id
            }))}
            rowKey='_id'
            bordered
          />
        </Card>
      ) : (
        <Card className='p-6 text-center'>
          <Empty
            description='Không có người dùng nào. Hãy thêm người dùng mới!'
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Link to='/admin/users/new'>
              <Button
                type='primary'
                icon={<PlusOutlined />}
                className='mt-4 bg-green-600 border-green-600 hover:bg-green-700'
              >
                Thêm người dùng mới
              </Button>
            </Link>
          </Empty>
        </Card>
      )}
    </div>
  )
}

export default Users
